import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { PhoneCall, Power, RadioTower, Sparkles } from 'lucide-react';
import { HandsetState, handsetStateLabel } from '../../data/deckSlide7';
interface NetworkPathDiagramProps {
  /** How far the call path has been drawn: 0 none, 1 to network, 2 to Vexu. */
  pathStage: 0 | 1 | 2;
  handsetVisible: boolean;
  handsetState: HandsetState;
  /** Emphasises that Vexu is still handling the call. */
  emphasise: boolean;
}

/** Settles from a brief lift into its resting state — no dramatic motion. */
const settle = {
  initial: {
    opacity: 0,
    y: 6,
    scale: 0.985
  },
  animate: {
    opacity: [0, 1, 1],
    scale: [0.985, 1.035, 1],
    y: 0
  }
};
const ease = [0.22, 0.61, 0.36, 1] as const;
const INK = '#0B1712';
const MUTED = '#8A998F';
const HAIRLINE = 'rgba(11,23,18,0.14)';

/** One node on the call path. */
function Node({
  cx,
  cy,
  r,
  active,
  strong,
  icon: Icon,
  label,
  sub,
  delay
}: {
  cx: number;
  cy: number;
  r: number;
  active: boolean;
  strong?: boolean;
  icon: React.ElementType;
  label: string;
  sub?: string;
  delay: number;
}) {
  return <motion.div className="absolute" style={{
    left: cx - r,
    top: cy - r,
    width: r * 2,
    height: r * 2
  }} initial={{
    opacity: 0,
    scale: 0.94
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.7,
    delay,
    ease
  }} data-id="element-249" data-mp="2b68y1hoq7ii0">
      <div className="grid h-full w-full place-items-center rounded-full transition-colors duration-700" style={{
      border: `1px solid ${active ? strong ? 'rgba(0,200,83,0.55)' : 'rgba(11,23,18,0.22)' : HAIRLINE}`,
      backgroundColor: strong ? 'rgba(0,200,83,0.08)' : 'rgba(255,255,255,0.7)'
    }} data-id="element-250" data-mp="1nhpq539l97qg">
        <Icon className="transition-colors duration-700" style={{
        width: strong ? 30 : 24,
        height: strong ? 30 : 24,
        color: strong ? 'rgb(0,150,63)' : active ? INK : MUTED
      }} strokeWidth={1.6} data-id="element-251" data-mp="1jgcl2vebl8kl" />
      </div>
      <div className="absolute left-1/2 -translate-x-1/2 whitespace-nowrap text-center" style={{
      top: r * 2 + 14
    }} data-id="element-252" data-mp="qeuwc646177k">
        <p className="text-[13px] font-semibold tracking-[-0.01em]" style={{
        color: strong ? 'rgb(0,120,52)' : INK
      }} data-id="element-253" data-mp="gni7zohksz6a">
          {label}
        </p>
        {sub && <p className="mt-1 text-[11.5px]" style={{
        color: MUTED
      }} data-id="element-254" data-mp="19c5ufne29ulf">
            {sub}
          </p>}
      </div>
    </motion.div>;
}

/**
 * Caller → telecom network → Vexu, with the user's handset off to the side.
 * The handset is never on the call path — that is the whole point.
 */
export function NetworkPathDiagram({
  pathStage,
  handsetVisible,
  handsetState,
  emphasise
}: NetworkPathDiagramProps) {
  const cy = 430;
  const callerX = 74;
  const networkX = 384;
  const vexuX = 706;
  const off = handsetState === 'off';
  const unavailable = handsetState !== 'available';
  const segment = (from: number, to: number, shown: boolean, delay: number) => <div className="absolute" style={{
    left: from,
    top: cy - 1,
    width: to - from,
    height: 2
  }} data-id="element-255" data-mp="1ul0iietc3mcp">
      <div className="absolute inset-0 rounded-full" style={{
      backgroundColor: 'rgba(11,23,18,0.10)'
    }} data-id="element-256" data-mp="1zjtzih7txvhm" />
      <motion.div className="absolute inset-y-0 left-0 rounded-full" style={{
      backgroundColor: 'rgba(0,200,83,0.55)'
    }} initial={{
      width: 0
    }} animate={{
      width: shown ? '100%' : 0
    }} transition={{
      duration: 0.75,
      delay,
      ease
    }} data-id="element-257" data-mp="29chtz5bc7nd6" />
    </div>;
  return <div className="relative" style={{
    width: 860,
    height: 620
  }} data-id="element-258" data-mp="2nb90a5jyk4qr">
      {/* Handset — beside the path, not in it */}
      <AnimatePresence data-id="element-259" data-mp="2s9sn086dlkk6">
        {handsetVisible && <motion.div className="absolute" style={{
        left: vexuX - 63,
        top: 34
      }} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.7,
        ease
      }} data-id="element-260" data-mp="sxq0ep2gga2a">
            <div className="relative rounded-[22px] p-[6px] transition-all duration-700" style={{
          width: 126,
          height: 224,
          backgroundColor: off ? 'rgba(11,23,18,0.10)' : '#FFFFFF',
          border: `1px solid ${off ? 'rgba(11,23,18,0.16)' : 'rgba(11,23,18,0.12)'}`,
          boxShadow: off ? 'none' : '0 18px 40px -28px rgba(11,23,18,0.45)',
          opacity: unavailable ? 0.72 : 1
        }} data-id="element-261" data-mp="2ofre1sli22y5">
              <div className="grid h-full w-full place-items-center rounded-[17px] transition-colors duration-700" style={{
            backgroundColor: off ? '#151E19' : '#F4F8F3'
          }} data-id="element-262" data-mp="2egsb8qnf11j2">
                {off ? <Power className="h-6 w-6" style={{
              color: 'rgba(255,255,255,0.42)'
            }} strokeWidth={1.6} data-id="element-263" data-mp="1hydvuxjooo8p" /> : <div className="flex flex-col items-center gap-2" data-id="element-264" data-mp="1ckw5nud31hkt">
                    <span className="block rounded-full transition-colors duration-500" style={{
                width: 9,
                height: 9,
                backgroundColor: unavailable ? 'rgba(180,84,58,0.6)' : 'rgba(0,200,83,0.85)'
              }} data-id="element-265" data-mp="xvzwtv37c0j5" />
                    <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em]" style={{
                color: MUTED
              }} data-id="element-266" data-mp="1gmr14i8c10qo">
                      Your phone
                    </span>
                  </div>}
              </div>
            </div>

            {/* Current state */}
            <div className="mt-3 flex justify-center" data-id="element-267" data-mp="a7glt7srp9nd">
              <AnimatePresence mode="wait" data-id="element-268" data-mp="2ct3weihw0ee4">
                <motion.span key={handsetState} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.6,
              ease
            }} className="rounded-full px-3 py-1 text-[11.5px] font-semibold tracking-[-0.01em]" style={{
              backgroundColor: unavailable ? 'rgba(180,84,58,0.10)' : 'rgba(0,200,83,0.10)',
              color: unavailable ? '#8C4531' : 'rgb(0,120,52)'
            }} data-id="element-269" data-mp="2cux3fywex0mn">
                  {handsetStateLabel[handsetState]}
                </motion.span>
              </AnimatePresence>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Handset ↔ Vexu: visibility and control, never call processing */}
      <AnimatePresence data-id="element-270" data-mp="39h0iu400a3x9">
        {handsetVisible && <motion.div className="absolute" style={{
        left: vexuX - 1,
        top: 296,
        width: 2,
        height: 88
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: off ? 0.3 : 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6,
        ease
      }} data-id="element-271" data-mp="3h1kuttq814bu">
            <div className="h-full w-full" style={{
          backgroundImage: 'repeating-linear-gradient(to bottom, rgba(11,23,18,0.26) 0 4px, transparent 4px 9px)'
        }} data-id="element-272" data-mp="3vq78lr079o0y" />
          </motion.div>}
      </AnimatePresence>
      <AnimatePresence data-id="element-273" data-mp="380cuz7823vfn">
        {handsetVisible && <motion.p className="absolute text-right text-[11.5px] leading-relaxed" style={{
        left: vexuX - 320,
        top: 312,
        width: 296,
        color: MUTED
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: off ? 0.45 : 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.6,
        ease
      }} data-id="element-274" data-mp="3d0r1gku0exbk">
            Visibility &amp; control when available
          </motion.p>}
      </AnimatePresence>

      {/* The call path */}
      {segment(callerX + 40, networkX - 42, pathStage >= 1, 0)}
      {segment(networkX + 42, vexuX - 54, pathStage >= 2, 0.1)}

      {/* Travelling signal */}
      {pathStage >= 2 && <motion.span className="absolute rounded-full" style={{
      top: cy - 4,
      width: 8,
      height: 8,
      backgroundColor: 'rgb(0,200,83)',
      boxShadow: '0 0 0 6px rgba(0,200,83,0.14)'
    }} initial={{
      left: callerX + 40,
      opacity: 0
    }} animate={{
      left: vexuX - 54,
      opacity: [0, 1, 1, 0]
    }} transition={{
      duration: 2.4,
      repeat: Infinity,
      ease: 'linear',
      times: [0, 0.08, 0.9, 1]
    }} data-id="element-275" data-mp="1ic41770y21o6" />}

      <Node cx={callerX} cy={cy} r={40} active={pathStage >= 1} icon={PhoneCall} label="Caller" delay={0} data-id="element-276" data-mp="6rynb656f336" />
      <Node cx={networkX} cy={cy} r={42} active={pathStage >= 1} icon={RadioTower} label="Telecom network" sub="Call diverted" delay={0.5} data-id="element-277" data-mp="2nd3on1r5jv5x" />
      <Node cx={vexuX} cy={cy} r={54} active={pathStage >= 2} strong={pathStage >= 2} icon={Sparkles} label="VEXU AI" delay={1.1} data-id="element-278" data-mp="2dlza235e2cbm" />

      {/* Vexu keeps handling the call */}
      <AnimatePresence data-id="element-279" data-mp="24jzrh6eq706k">
        {emphasise && <motion.div className="absolute flex flex-col items-center" style={{
        left: vexuX - 150,
        top: cy + 108,
        width: 300
      }} initial={settle.initial} animate={settle.animate} exit={{
        opacity: 0
      }} transition={{
        duration: 1.1,
        times: [0, 0.45, 1],
        ease
      }} data-id="element-280" data-mp="1pq4xt7n55hfy">
            <span className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5" style={{
          backgroundColor: 'rgba(0,200,83,0.12)',
          boxShadow: '0 0 0 6px rgba(0,200,83,0.06)'
        }} data-id="element-281" data-mp="3p039houqwso2">
              <motion.span className="block h-[7px] w-[7px] rounded-full" style={{
            backgroundColor: 'rgb(0,200,83)'
          }} animate={{
            opacity: [1, 0.35, 1]
          }} transition={{
            duration: 1.6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-282" data-mp="1ko6hoy4xcryj" />
              <span className="text-[11.5px] font-semibold uppercase tracking-[0.18em]" style={{
            color: 'rgb(0,120,52)'
          }} data-id="element-283" data-mp="2i9vimqc8l3sy">
                Still handling the call
              </span>
            </span>
            <p className="mt-4 text-[12px] uppercase tracking-[0.2em]" style={{
          color: MUTED
        }} data-id="element-284" data-mp="ry7r2yb30mrr">
              Understand · Act · Escalate · Remember
            </p>
          </motion.div>}
      </AnimatePresence>
    </div>;
}